import {
  Badge,
  Box,
  Button,
  Card,
  Text,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  IconButton,
  theme,
  Stack,
} from "@aksara-ui/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllTenantsFilter,
  getAllTiers,
  setModalConfirmationOpen,
  setModalFormOpen,
} from "redux/actions";
import styled from "styled-components";
import { toCapitilizeCase } from "utilities/helper/toCapitalizeCase";
import { TierConfirmModal, TierFormModal } from "./components";
import {
  IconAwards,
  IconCloudDownload,
  IconPen,
  IconPlusRounded,
  IconTrash,
} from "@aksara-ui/icons";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import { exportToCSV } from "./components/exportToCSV";
import { useMediaQuery } from "react-responsive";
import { Link } from "react-router-dom";
import FilterKit from "components/FilterKit";
import { getRole } from "hooks";
import { LazyLoadImage } from "react-lazy-load-image-component";

const HeaderWrapper = styled(Box)`
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 20px;
`;

const TiersContent: React.FC = () => {
  const dispatch = useDispatch();
  const userRole = getRole();
  const isMobile = useMediaQuery({ maxWidth: 576 });
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  const { appUrl, isLoading, isModalFormOpen, isModalConfirmationOpen } =
    useSelector((state: any) => state.globalReducer);
  const { tiers } = useSelector((state: any) => state.tierReducer);
  const { tenantsFilter } = useSelector((state: any) => state.tenantReducer);

  const [selectedTierId, setSelectedTierId] = useState("");

  const handleOpenFormModal = (tierId: string = "") => {
    dispatch(setModalFormOpen(true) as any);
    setSelectedTierId(tierId);
  };

  const handleCloseFormModal = () => {
    dispatch(setModalFormOpen(false) as any);
    setSelectedTierId("");
  };

  const handleOpenConfirmModal = (tierId: string) => {
    dispatch(setModalConfirmationOpen(true) as any);
    setSelectedTierId(tierId);
  };

  const handleCloseConfirmModal = () => {
    dispatch(setModalConfirmationOpen(false) as any);
    setSelectedTierId("");
  };

  useEffect(() => {
    if (!tiers.length) {
      dispatch(getAllTenantsFilter() as any);
      dispatch(getAllTiers() as any);
    }
  }, [dispatch]);

  return (
    <Box height={"100%"} paddingY={20} paddingX={isMobile ? 30 : 50}>
      <HeaderWrapper
        display={"flex"}
        flexDirection={"column"}
        style={{ gap: 20 }}
      >
        <Box display="flex">
          <Text scale={[500, 600]} fontWeight={700}>
            Tiering Membership
          </Text>
        </Box>

        <Box
          display={"flex"}
          flexDirection={isTablet ? "column" : "row"}
          justifyContent={userRole === "superadmin" ? "space-between" : "right"}
          style={{ gap: 10 }}
        >
          {userRole === "superadmin" ? (
            <Box
              display={"flex"}
              flexDirection={isTablet ? "column" : "row"}
              justifyContent={"space-between"}
              style={{ gap: 5 }}
            >
              <FilterKit
                orderByOptions={[
                  {
                    key: tenantsFilter,
                    label: "Tenant",
                    options: tenantsFilter.map((tenant: any) => {
                      return {
                        label: tenant.name,
                        value: tenant.id,
                      };
                    }),
                  },
                ]}
                onChangeOrder={(e: any) => {
                  dispatch(
                    getAllTiers(
                      e[tenantsFilter] ? e[tenantsFilter].value : ""
                    ) as any
                  );
                }}
              />
            </Box>
          ) : (
            <></>
          )}

          {userRole === "superadmin" ? (
            <Stack
              display={"flex"}
              justifyContent={"right"}
              flexDirection={"row"}
              // maxWidth={180}
              style={{ gap: 20 }}
            >
              <Box>
                <Button
                  variant="outline"
                  borderRadius={10}
                  style={{
                    fontWeight: 600,
                    color: "white",
                    backgroundColor: `${theme.colors.green07}`,
                  }}
                  icon={IconCloudDownload}
                  iconPosition={"left"}
                  size={32}
                  onClick={() => exportToCSV(tiers)}
                >
                  Export to CSV
                </Button>
              </Box>

              <Button
                variant="primary"
                borderRadius={10}
                style={{ fontWeight: 600 }}
                icon={IconPlusRounded}
                iconPosition={"left"}
                size={32}
                onClick={() => handleOpenFormModal()}
              >
                Add Tier
              </Button>
            </Stack>
          ) : (
            <></>
          )}
        </Box>
      </HeaderWrapper>

      <Card maxHeight={"100%"} overflow={"auto"} elevation={2}>
        <Table>
          <TableHead
            position={"sticky"}
            top={0}
            backgroundColor={theme.colors.grey02}
          >
            <TableHeadRow>
              <TableHeadCell>Tier</TableHeadCell>
              {/* <TableHeadCell>Icon</TableHeadCell> */}
              <TableHeadCell>Card</TableHeadCell>
              <TableHeadCell>Points Low</TableHeadCell>
              <TableHeadCell>Points High</TableHeadCell>
              {userRole === "superadmin" ? (
                <TableHeadCell>Action</TableHeadCell>
              ) : (
                <></>
              )}
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodyRowsSkeleton
                rowsNumber={10}
                columnsNumber={userRole === "superadmin" ? 5 : 4}
              />
            ) : !tiers.length ? (
              <TableBodyRow>
                <TableBodyCell
                  colSpan={userRole === "superadmin" ? 5 : 4}
                  textAlign={"center"}
                >
                  No data available in table
                </TableBodyCell>
              </TableBodyRow>
            ) : (
              tiers.map((tier: any) => {
                return (
                  <TableBodyRow key={tier.id}>
                    <TableBodyCell whiteSpace={"nowrap"}>
                      <Badge
                        size="md"
                        style={{
                          backgroundImage: `${tier.tier_background}`,
                          color: `${
                            tier.tier_name !== "silver" ? "#FAFBFD" : "#949DA7"
                          }`,
                        }}
                      >
                        {toCapitilizeCase(tier.tier_name)}
                      </Badge>
                    </TableBodyCell>
                    {/* <TableBodyCell>
                      {tier.tier_icon ? (
                        <LazyLoadImage
                          key={tier.id}
                          src={`${appUrl}/storage/${tier.tier_icon}`}
                          alt="tier-icon"
                          effect="blur"
                        />
                      ) : (
                        ""
                      )}
                    </TableBodyCell> */}
                    <TableBodyCell>
                      {tier.tier_card_template ? (
                        <LazyLoadImage
                          key={tier.id}
                          src={`${appUrl}/storage/${tier.tier_card_template}`}
                          alt="tier-card"
                          height={"100%"}
                          width={180}
                          style={{ borderRadius: 20 }}
                          effect="blur"
                        />
                      ) : (
                        ""
                      )}
                    </TableBodyCell>
                    <TableBodyCell>
                      <Badge
                        size="md"
                        variant="critical"
                        style={{
                          paddingLeft: 20,
                          paddingRight: 20,
                        }}
                      >
                        {tier.points_low.toLocaleString()}
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell>
                      <Badge
                        size="md"
                        variant="success"
                        style={{
                          paddingLeft: 20,
                          paddingRight: 20,
                        }}
                      >
                        {tier.points_high.toLocaleString()}
                      </Badge>
                    </TableBodyCell>
                    {userRole === "superadmin" ? (
                      <TableBodyCell>
                        <Link to={`rewards/${tier.tier_name}`}>
                          <IconButton>
                            <IconAwards fill={theme.colors.blue06} />
                          </IconButton>
                        </Link>
                        <IconButton
                          onClick={() => handleOpenFormModal(tier.id)}
                        >
                          <IconPen fill={theme.colors.green06} />
                        </IconButton>
                        <IconButton
                          onClick={() => handleOpenConfirmModal(tier.id)}
                        >
                          <IconTrash fill={theme.colors.red06} />
                        </IconButton>
                      </TableBodyCell>
                    ) : (
                      <></>
                    )}
                  </TableBodyRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <TierFormModal
        tierId={selectedTierId}
        isOpen={isModalFormOpen}
        onClose={() => handleCloseFormModal()}
      />

      <TierConfirmModal
        tierId={selectedTierId}
        isOpen={isModalConfirmationOpen}
        onClose={() => handleCloseConfirmModal()}
      />
    </Box>
  );
};

export default TiersContent;
